/*
  (c) 2025 Fluxonix Inc.
*/

import React from "react";
import {
  Box, Card, CardContent, Typography, Divider, CircularProgress, Alert,
  Switch, Chip, List, ListItem, ListItemText, ListItemSecondaryAction,
  Stack, Button
} from "@mui/material";
import { useAgents } from "../hooks/useAgents";
import { useToggleAgent } from "../hooks/useToggleAgent";
import RequireRole from "../components/RequireRole";

export default function AdminAgents() {
  const { agents, loading, err, setAgents } = useAgents();
  const { toggleAgent, loadingId, error: toggleErr } = useToggleAgent(setAgents);

  const onToggle = async (name, enabled) => {
    try {
      await toggleAgent(name, enabled);
      setAgents(prev =>
        prev.map(a => (a.name === name ? { ...a, status: enabled } : a))
      );
    } catch (e) {
      console.error("❌ Toggle failed:", e);
    }
  };

  if (loading) {
    return (
      <Box p={4} display="flex" alignItems="center" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <RequireRole role="admin">
      <Box p={4}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h5">⚙️ Agent Management</Typography>
          <Button onClick={() => window.location.reload()} variant="outlined" size="small">
            Refresh
          </Button>
        </Stack>

        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}

        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>Registered Agents</Typography>
            <Divider sx={{ mb: 2 }} />

            {agents.length === 0 ? (
              <Typography color="text.secondary">No agents found.</Typography>
            ) : (
              <List>
                {agents.map(agent => (
                  <ListItem key={agent.name} disableGutters divider>
                    <ListItemText
                      primary={
                        <strong>
                          {agent.default_pipeline_position
                            ? `${agent.default_pipeline_position}. `
                            : ""}
                          {agent.name}
                        </strong>
                      }
                      secondary={
                        <>
                          {agent.description} <br />
                          <Typography variant="caption" color="text.secondary">
                            Category: {agent.category}
                          </Typography>
                        </>
                      }
                    />

                    <Chip
                      label={agent.status ? "Enabled" : "Disabled"}
                      color={agent.status ? "success" : "default"}
                      size="small"
                      sx={{ mr: 1 }}
                    />

                    <ListItemSecondaryAction>
                      <Switch
                        edge="end"
                        checked={Boolean(agent.status)}
                        onChange={(e) => onToggle(agent.name, e.target.checked)}
                        disabled={!agent.configurable || loadingId === agent.name}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </CardContent>
        </Card>

        {toggleErr && <Alert severity="error" sx={{ mt: 2 }}>{toggleErr}</Alert>}
      </Box>
    </RequireRole>
  );
}